import { Button, Select, Spin } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FiEye } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import DataTable from "@/components/DataTable/DataTable";
import { AssignedDeviceFormula } from "./AssignedDeviceFormula";
import { userRole } from "@/constant";
import { numberFormatter } from "@/utils";
import { DeviceFormulaOperation } from "./DeviceFormulaOperation";
import DeviceParameters from "./DeviceParameters";
import { flare } from "@flareapp/js";

const DeviceDetails = () => {
  const userDetails = useSelector((state) => state.user?.userDetails || {});
  const params = useParams();
  const navigate = useNavigate();
  const deviceId = params.deviceId;
  const [isDeviceLoading, setDeviceIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isFormulaLoading, setIsFormulaLoading] = useState(false);
  const [deviceDetails, setDeviceDetails] = useState();
  const [deviceParameters, setDeviceParameters] = useState([]);
  const [systemFormulas, setSystemFormulas] = useState([]);
  const [deviceFormulas, setDeviceFormulas] = useState([]);
  const [selectedFormulaId, setSelectedFormulaId] = useState("");
  const [selectedFormula, setSelectedFormula] = useState();
  const [tariffInfo, setTariffInfo] = useState({});

  const initialState = {
    name: "",
    unit: "",
    formula: "",
    device: deviceId,
    type: "device",
    operations: [],
    intervalPeriod: "",
    fallbackBehavior: "",
    operator: "",
    intervalPeriodCount: "",
  };

  const [formulaData, setFormulaData] = useState(initialState);

  function handleChange(value, name) {
    // console.log(value, name);
    setFormulaData({
      ...formulaData,
      [name]: value,
    });
  }

  const onChangeOperation = (index, obj) => {
    const data = Array.from(formulaData.operations);

    data[index] = { ...data[index], ...obj };

    setFormulaData({
      ...formulaData,
      operations: data,
    });
  };
  const getTariffInfo = async () => {
    setDeviceIsLoading(true);
    try {
      const result = await api.get("/device/tariff-data", {
        params: {
          device: deviceId,
          alon: "io",
        },
      });
      setTariffInfo(result);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setDeviceIsLoading(false);
  };

  useEffect(() => {
    if (selectedFormulaId) {
      const systemFormula = systemFormulas.find((item) => String(item._id) === String(selectedFormulaId));
      if (systemFormula) {
        setSelectedFormula(systemFormula);
      }
    } else {
      setSelectedFormula();
    }
  }, [selectedFormulaId]);

  useEffect(() => {
    if (selectedFormula) {
      setFormulaData({
        ...formulaData,
        name: `${deviceDetails?.name} - ${selectedFormula?.name}`,
        unit: selectedFormula?.unit,
        formula: selectedFormula?.formula,
        operations: selectedFormula.operations,
      });
    }
    getAllParameters();
  }, [selectedFormula, deviceDetails]);

  const getDevice = async () => {
    setDeviceIsLoading(true);
    try {
      const response = await axios.get(`/device/${deviceId}`, {
        withCredentials: true,
      });
      if (response) {
        setDeviceDetails(response.data.result);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setDeviceIsLoading(false);
  };

  const getAllParameters = async () => {
    try {
      const response = await api.get(endpoint.get_device_parameter, {
        params: {
          limit: 10000,
          page: 1,
          deviceId:
            deviceDetails?.devices?.length && deviceDetails?.deviceType?.name === "Virtual Device"
              ? deviceDetails?.devices.map(({ _id }) => _id)
              : deviceId,
        },
      });
      setDeviceParameters(response?.results || []);
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const getSystemFormulas = async () => {
    const response = await axios.get(`/formula`, { withCredentials: true });
    if (response) {
      setSystemFormulas(response.data?.result);
    }
  };

  const getDeviceFormulas = async () => {
    setIsFormulaLoading(true);
    const response = await axios.get(`/formula/`, {
      withCredentials: true,
      params: { device: deviceId, type: "device" },
    });
    if (response) {
      setDeviceFormulas(
        Array.isArray(response.data?.result)
          ? response.data?.result?.map((item, idx) => {
              item.idx = idx + 1;
              return item;
            })
          : [],
      );
    }
    setIsFormulaLoading(false);
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Device Information`;
    getDevice();
    getSystemFormulas();
    getDeviceFormulas();
    if (deviceId) {
      getTariffInfo();
    }
  }, [deviceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitLoading(true);
    const { intervalPeriod = 0, intervalPeriodCount = 0 } = formulaData;
    const body = {
      ...formulaData,
      intervalPeriod: intervalPeriod * intervalPeriodCount,
      systemFormula: selectedFormulaId,
    };

    try {
      await axios.post(`/formula`, body, {
        withCredentials: true,
      });
      setSelectedFormulaId("");
      setFormulaData(initialState);
      getDeviceFormulas();
      getAllParameters();
      Swal.fire({
        title: "Done!",
        text: "Formula Created Successfully",
        icon: "success",
        timer: 2000,
        confirmButtonText: "OK",
      });
    } catch (error) {
      flare.report(error);
      Swal.fire({
        title: "",
        text: error.response?.data?.message || error.message || "Something went wrong",
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
    setIsSubmitLoading(false);
  };

  const deleteDeviceFormula = async (Id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to unassign formula?",
      dangerMode: true,
      showCancelButton: true,
      confirmButtonText: "Confirm",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`/formula/` + Id, {
            withCredentials: true,
          })
          .then(() => {
            getDeviceFormulas();
            getAllParameters();
            Swal.fire({
              title: "Done!",
              text: "Formula unassigned successfully",
              icon: "success",
              timer: 2000,
              button: false,
            });
          });
      }
    });
  };

  useEffect(() => {
    let formula = "";
    for (const op of formulaData.operations) {
      if (["time", "area", "year", "month"].includes(op.value)) {
        formula += " " + op.value[0].toUpperCase() + op.value.slice(1);
      } else {
        formula += " " + op.value;
      }
    }
    setFormulaData({ ...formulaData, formula: formula.trim() });
  }, [formulaData.operations]);

  return (
    <Spin spinning={isDeviceLoading}>
      <div className="card p-3 m-md-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <label className="az-content-label">Manage Devices</label>
            <span className="d-block py-2">Device Information</span>
          </div>
        </div>
        <hr className="mt-0" />
        <div className="row mt-2">
          <div className="col-md-6">
            <h5 className="mb-4">General Description</h5>
            <div className="row mb-2">
              <div className="col-3">Device Name</div>
              <div className="col-9">: {deviceDetails?.name}</div>
            </div>
            <div className="row mb-2">
              <div className="col-3">Device Type</div>
              <div className="col-9">: {deviceDetails?.deviceType?.name}</div>
            </div>

            {deviceDetails?.deviceType?.name !== "Virtual Device" ? (
              <>
                <div className="row mb-2">
                  <div className="col-3">Serial No.</div>
                  <div className="col-9">: {deviceDetails?.serial}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-3">API Key</div>
                  <div className="col-9">: {deviceDetails?.apiKey}</div>
                </div>
              </>
            ) : (
              ""
            )}
            <div className="row mb-2">
              <div className="col-3">Installed Area</div>
              <div className="col-9">: {deviceDetails?.installedArea}</div>
            </div>
            <div className="row mb-2">
              <div className="col-3">Site Location</div>
              <div className="col-9">: {deviceDetails?.site?.name}</div>
            </div>

            <div className="row mb-2">
              <div className="col-3">Business</div>
              <div className="col-9">: {deviceDetails?.business ? `${deviceDetails?.business?.name} (${deviceDetails?.business?.id})` : ""}</div>
            </div>
            {deviceDetails?.hasTariff && deviceDetails?.electricityTariff?.name && (
              <div className="row mb-2">
                <div className="col-3">Electricity Tariff Category</div>
                <div className="col-9">: {deviceDetails?.electricityTariff?._id ? `${deviceDetails?.electricityTariff?.name}` : ""}</div>
              </div>
            )}
          </div>
          {deviceDetails?.hasTariff && deviceDetails?.electricityTariff?.name && (
            <div className="col-md-6">
              <div className="row">
                <div className="col-md-12">
                  <b>Electricity Tariff Information</b>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6">Month: {moment().format("MMMM YYYY")}</div>
                <div className="col-md-6">Tariff Category: {deviceDetails?.electricityTariff?.name?.split("-")?.[0] || ""}</div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0" }}>Current Electricity Usage</p>
                </div>
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0", textAlign: "end" }}>
                    RM{" "}
                    <span style={{ display: "inline-block", marginRight: "10px" }}>
                      {numberFormatter(tariffInfo?.electricityTariff?.totalPrice || 0)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0" }}>Current Maximum Demand</p>
                </div>
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0", textAlign: "end" }}>
                    RM{" "}
                    <span style={{ display: "inline-block", marginRight: "10px" }}>
                      {numberFormatter(tariffInfo?.maximumDemandCost?.totalPrice || 0)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0" }}>Total Electricity Cost</p>
                </div>
                <div className="col-md-6">
                  <p style={{ padding: "5px 5px", backgroundColor: "#e0e0e0", textAlign: "end" }}>
                    RM{" "}
                    <span style={{ display: "inline-block", marginRight: "10px" }}>
                      {numberFormatter((tariffInfo?.electricityTariff?.totalPrice || 0) + (tariffInfo?.maximumDemandCost?.totalPrice || 0))}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="col-md-6">
            {deviceDetails?.devices?.length > 0 && (
              <DataTable
                columns={[
                  {
                    title: "Serial No",
                    render: (_, row, index) => <div>{index + 1}</div>,
                    center: true,
                    width: "110px",
                  },
                  { title: "Device Name", render: (row) => row.name },
                  { title: "API Key", render: (row) => row.apiKey },
                  {
                    title: "Action",
                    render: (row) => (
                      <Link to={`/devices/details/` + row._id} className="btn btn-info me-1">
                        <FiEye />
                      </Link>
                    ),
                  },
                ]}
                dataSource={deviceDetails?.devices}
                paginationRowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-md-12 d-flex justify-content-end">
            {userDetails?.role !== userRole.public && (
              <Link to={`/devices/edit/${deviceDetails?._id}`} className="me-2">
                <Button type="primary">Edit</Button>
              </Link>
            )}
            <Button
              onClick={() => {
                navigate(-1);
              }}
              type="primary"
              className="bg-secondary"
            >
              Back
            </Button>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="row mb-2">
            <div className="col-md-12">
              <DeviceParameters
                isVirtualDevice={deviceDetails?.deviceType?.name === "Virtual Device"}
                device={deviceDetails}
                hasTariff={deviceDetails?.hasTariff}
                hasWaterTariff={deviceDetails?.hasWaterTariff}
              />
            </div>
            <div className="col-md-12">
              <AssignedDeviceFormula
                showPeriodAndFallback={true}
                deviceFormulas={deviceFormulas}
                deleteFormula={deleteDeviceFormula}
                deviceParameters={deviceParameters}
                refetchFormula={getDeviceFormulas}
                isLoading={isFormulaLoading}
              />
            </div>
          </div>
          {![userRole.public].includes(userDetails?.role) && (
            <div className="row mt-5">
              <form onSubmit={handleSubmit}>
                <h5 className="m-0">Assign Formula to Device</h5>
                <div className="d-flex justify-content-center">{isSubmitLoading && <Spinner animation="border" variant="dark" />}</div>
                <div className="row me-3">
                  <div className="col-md-3 mt-3">
                    <label htmlFor="formula">Assign Formula</label>
                    <Select
                      className="d-flex"
                      size="large"
                      value={selectedFormulaId || null}
                      onChange={(value) => setSelectedFormulaId(value)}
                      required
                      placeholder="Select a Formula"
                    >
                      {systemFormulas.length &&
                        systemFormulas.map((item, index) => (
                          <option value={item?._id} key={index}>
                            {item.name}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <div className="col-md-3 mt-3">
                    <label htmlFor="formula">Interval Period</label>
                    <Select
                      className="d-flex"
                      size="large"
                      value={formulaData?.intervalPeriod || null}
                      onChange={(e) => handleChange(e, "intervalPeriod")}
                      placeholder="Select Interval Period"
                      required
                      options={[
                        { label: "Daily", value: 1440 },
                        { label: "Weekly", value: 10080 },
                        { label: "Monthly", value: 43830 },
                        { label: "Yearly", value: 525960 },
                        // { label: "Last X Day", value: 1441 },
                        { label: "Minute", value: 1 },
                        { label: "Hourly", value: 60 },
                      ]}
                    >
                      {/* <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="lastXDays">Last X Days</option>
                    <option value="minute">Minute</option>
                    <option value="hourly">Hourly</option> */}
                    </Select>
                  </div>
                  <div className="col-md-3 mt-3">
                    <label htmlFor="formula">Interval Period Value</label>
                    <input
                      type="number"
                      className="form-control"
                      name="intervalPeriodCount"
                      value={formulaData?.intervalPeriodCount || ""}
                      onChange={(e) => {
                        let value = Number(e.target.value || 0);
                        if (value < 1) value = 1;
                        handleChange(value, "intervalPeriodCount");
                      }}
                      placeholder="Enter Interval Value"
                      required
                      min={1}
                    />
                  </div>
                  <div className="col-md-3 mt-3">
                    <label htmlFor="formula">Operator</label>
                    <Select
                      className="d-flex"
                      size="large"
                      value={formulaData?.operator || null}
                      onChange={(e) => handleChange(e, "operator")}
                      placeholder="Select Operator"
                      required
                    >
                      <option value="real">Real Time</option>
                      <option value="max">Maximum</option>
                      <option value="min">Minimum</option>
                      <option value="avg">Average</option>
                      <option value="sum">Sum</option>
                      <option value="differential">Differential</option>
                    </Select>
                  </div>
                  <div className="col-md-4 mt-3">
                    <label htmlFor="formula">Action On Data Not Found</label>
                    <Select
                      className="d-flex"
                      size="large"
                      name="fallbackBehavior"
                      value={formulaData?.fallbackBehavior || null}
                      onChange={(e) => handleChange(e, "fallbackBehavior")}
                      required
                      placeholder="Select Action"
                    >
                      <option value={"Zero(0)"}>Zero(0)</option>
                      <option value={"Skip"}>Skip</option>
                    </Select>
                  </div>

                  <div className="col-md-4 mt-3">
                    <label htmlFor="formula">Formula Preview</label>
                    <input type="text" className="form-control" value={formulaData.formula} disabled />
                  </div>
                  <div className="col-md-4 mt-3">
                    <label htmlFor="formula">Formula Unit/Value</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formulaData?.unit}
                      onChange={(e) => handleChange(e.target.value, "unit")}
                      disabled
                    />
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", flex: 1, overflowX: "auto", paddingTop: 15, paddingBottom: 15 }}>
                    <div
                      className=" gap-3"
                      style={{
                        display: "flex",
                      }}
                    >
                      {Array.isArray(selectedFormula?.operations) &&
                        selectedFormula.operations.map((op, index) => {
                          const parameters = Array.isArray(deviceParameters)
                            ? deviceParameters.map((p) => ({
                                name: `${p.name} (${p?.device?.site?.name} > ${p?.device?.name})`,
                                value: `${p.name} (${p?.device?.site?.name} > ${p?.device?.name})`,
                                _id: p._id,
                              }))
                            : [];
                          return (
                            <DeviceFormulaOperation
                              key={index}
                              operation={op}
                              parameters={parameters}
                              formulas={deviceFormulas}
                              onChange={(obj) => onChangeOperation(index, obj)}
                              disabled={!["parameter", "formula"].includes(op.option)}
                              width={["parameter", "formula"].includes(op.option) ? "400px" : "150px"}
                            />
                          );
                        })}
                    </div>
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="row">
                    <div className="col-md-12">
                      <b>Device Formula Preview: {formulaData.formula}</b>
                    </div>
                  </div>
                  <div className="row mt-2 mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name">Formula Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formulaData.name}
                          onChange={(e) => handleChange(e.target.value, "name")}
                          className="form-control"
                          placeholder="Enter formula name"
                          required
                        />
                      </div>
                      <div>
                        <Button type="primary" className="me-2" htmlType="submit">
                          Save Formula
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default DeviceDetails;
